/**
 * mutualFundCardSamples.js — the starter documents behind the dialog's "Insert sample" menu.
 *
 * PURE ESM, and deliberately nothing but data: JSON documents as STRINGS, ready to drop into a
 * textarea verbatim. No Vue, no import of the validator, no builder that assembles them at runtime —
 * a starter that is assembled is a starter that can drift from the payload the backend actually
 * emits, and the whole value of these is that they ARE the real thing.
 *
 * ── Two kinds of document live here, and the difference is not stylistic ─────────────────────────
 * The three FUND CARDS (type8 / type9 / type10) are FIXED PAYLOADS: the admin edits those very
 * numbers and strings, nothing substitutes into them, and the test suite pins that they contain no
 * `{{ }}` and no `@repeat`. The two PICKERS (type14) are MOSTLY fixed — every word in them is a
 * literal the admin owns and translates — with ONE templated field, `list`, because the options are
 * whatever this turn's search found and no admin can type those in advance.
 *
 * There are TWO pickers, not one, and they are two documents of the SAME card type: one per mode
 * (`cp`), chosen by a node key each. That is what keeps `prefix` / `join` / `submit` editable and
 * translated instead of compiled into the server — see `PICKER_SAMPLE`'s note.
 *
 * ── Where the three fund cards come from, and why they are copies ────────────────────────────────
 * They are the payloads in `tasks/2026-08-30-mutualfund-card-config-list/samples/*.json`, which are
 * themselves the shapes `MutualFundArtifactProjection` produces (type8 / type9 / type10), re-printed
 * at two-space indent so they read in a textarea. They are COPIED rather than fetched because those
 * three cards are FIXED PAYLOADS: the admin edits these very numbers and strings by hand, and no
 * template engine touches them.
 *
 * 🔴 THE FILE IS BIG (~23 KB of string literal) AND THAT IS THE POINT. Trimming a sample to a
 * "representative" subset would hand the admin a document that renders half a card, and the first
 * thing they would learn is which fields we left out. Every field the renderer reads is present in
 * all three, including the per-field `*Color` / `*FontSize` hints that carry the design. (The pickers
 * are short for the opposite reason: their wire shape is EIGHT KEYS the owner fixed, and adding a
 * ninth is a contract break, not a richer sample.)
 */

/**
 * type8 — one fund's factsheet: symbol + fund name, the NAV block, the investment policy prose and
 * bullets, a stats table whose rows carry a `tone` (POSITIVE / NEGATIVE / DEFAULT), top holdings, the
 * disclaimer, and a footer link.
 */
export const FACTSHEET_SAMPLE = `{
  "type": "TAX_FUND_FACTSHEET",
  "version": 1,
  "refId": "type8",
  "data": {
    "symbol": "ONE-ELITE11RA",
    "symbolColor": "#1F2A44",
    "symbolFontSize": 14,
    "fundName": "กองทุนเปิด วัน อีลิท พอร์ต 11",
    "fundNameColor": "#1F2A44",
    "fundNameFontSize": 14,
    "iconUrl": "",
    "nav": {
      "label": "NAV ล่าสุด",
      "labelColor": "#1F2A44",
      "labelFontSize": 14,
      "value": 10.4521,
      "valueColor": "#8A8F9C",
      "valueFontSize": 14,
      "unit": "บาท",
      "change": 0.0842,
      "caption": "(ข้อมูล ณ 8 ส.ค. 2569 เทียบวันก่อนหน้า)",
      "captionColor": "#8A8F9C",
      "captionFontSize": 14
    },
    "policyTitle": "นโยบายการลงทุน",
    "policyTitleColor": "#1F2A44",
    "policyTitleFontSize": 16,
    "policyParagraphs": [
      "กองทุนมีนโยบายลงทุนโดยเฉลี่ยในรอบปีบัญชีไม่น้อยกว่า 80% ของ NAV ในตราสารทุนและ/หรือหุ้นสามัญ โดยมีสัดส่วนหรือน้ำหนักการลงทุนใกล้เคียง (Full Replication) กับดัชนี Bloomberg Global Industry Elite 55 Index (ดัชนีอ้างอิง) ซึ่งประกอบด้วยหุ้นจาก 11 อุตสาหกรรม"
    ],
    "policyParagraphsColor": "#1F2A44",
    "policyParagraphsFontSize": 14,
    "policyBullets": [
      "กองทุนอาจลงทุนในกองทุนรวมอื่นภายใต้การบริหารของบริษัทจัดการได้ไม่เกิน 100% ของ NAV และกองทุนรวมอื่นนั้นลงทุนต่อในกองทุนที่บริษัทจัดการเดียวกันดูแลได้อีกไม่เกิน 1 ทอด ทั้งนี้ กองทุนปลายทางไม่สามารถลงทุนย้อนกลับในกองทุนต้นทางได้"
    ],
    "policyBulletsColor": "#1F2A44",
    "policyBulletsFontSize": 14,
    "stats": [
      {
        "label": "ประเภท",
        "labelColor": "#1F2A44",
        "labelFontSize": 14,
        "value": "ผสม (หุ้น+ตราสารหนี้)",
        "valueColor": "#8A8F9C",
        "valueFontSize": 14,
        "tone": "DEFAULT"
      },
      {
        "label": "ระดับความเสี่ยง",
        "labelColor": "#1F2A44",
        "labelFontSize": 14,
        "value": "5",
        "valueColor": "#8A8F9C",
        "valueFontSize": 14,
        "tone": "DEFAULT"
      },
      {
        "label": "ผลตอบแทน 3 เดือน",
        "labelColor": "#1F2A44",
        "labelFontSize": 14,
        "value": "+2.15%",
        "valueColor": "#8A8F9C",
        "valueFontSize": 14,
        "tone": "POSITIVE"
      },
      {
        "label": "ผลตอบแทน 1 ปี",
        "labelColor": "#1F2A44",
        "labelFontSize": 14,
        "value": "+6.82%",
        "valueColor": "#8A8F9C",
        "valueFontSize": 14,
        "tone": "POSITIVE"
      },
      {
        "label": "ผลตอบแทนตั้งแต่จัดตั้งกองทุน",
        "labelColor": "#1F2A44",
        "labelFontSize": 14,
        "value": "+5.40%",
        "valueColor": "#8A8F9C",
        "valueFontSize": 14,
        "tone": "POSITIVE"
      },
      {
        "label": "SD (1 ปี)",
        "labelColor": "#1F2A44",
        "labelFontSize": 14,
        "value": "12.4%",
        "valueColor": "#8A8F9C",
        "valueFontSize": 14,
        "tone": "DEFAULT"
      },
      {
        "label": "Sharpe Ratio (1 ปี)",
        "labelColor": "#1F2A44",
        "labelFontSize": 14,
        "value": "0.58",
        "valueColor": "#8A8F9C",
        "valueFontSize": 14,
        "tone": "DEFAULT"
      },
      {
        "label": "Max Drawdown (1 ปี)",
        "labelColor": "#1F2A44",
        "labelFontSize": 14,
        "value": "-9.2%",
        "valueColor": "#8A8F9C",
        "valueFontSize": 14,
        "tone": "NEGATIVE"
      }
    ],
    "statsCaption": "ข้อมูล ณ 30 มิ.ย. 2569",
    "statsCaptionColor": "#8A8F9C",
    "statsCaptionFontSize": 14,
    "holdingsTitle": "สัดส่วนของการลงทุน",
    "holdingsTitleColor": "#1F2A44",
    "holdingsTitleFontSize": 16,
    "holdings": [
      {
        "name": "Apple Inc",
        "nameColor": "#1F2A44",
        "nameFontSize": 14,
        "percent": 14.85,
        "percentColor": "#8A8F9C",
        "percentFontSize": 14
      },
      {
        "name": "NVIDIA Corp",
        "nameColor": "#1F2A44",
        "nameFontSize": 14,
        "percent": 14.68,
        "percentColor": "#8A8F9C",
        "percentFontSize": 14
      },
      {
        "name": "Microsoft Corp",
        "nameColor": "#1F2A44",
        "nameFontSize": 14,
        "percent": 14.61,
        "percentColor": "#8A8F9C",
        "percentFontSize": 14
      },
      {
        "name": "Alphabet Inc Class A",
        "nameColor": "#1F2A44",
        "nameFontSize": 14,
        "percent": 10.9,
        "percentColor": "#8A8F9C",
        "percentFontSize": 14
      },
      {
        "name": "ASML Holding NV",
        "nameColor": "#1F2A44",
        "nameFontSize": 14,
        "percent": 5.21,
        "percentColor": "#8A8F9C",
        "percentFontSize": 14
      },
      {
        "name": "อื่นๆ",
        "nameColor": "#1F2A44",
        "nameFontSize": 14,
        "percent": 39.75,
        "percentColor": "#8A8F9C",
        "percentFontSize": 14
      }
    ],
    "holdingsCaption": "ข้อมูล ณ 30 มิ.ย. 2569 อาจเปลี่ยนแปลงตามดุลยพินิจผู้จัดการกองทุน",
    "holdingsCaptionColor": "#8A8F9C",
    "holdingsCaptionFontSize": 14,
    "disclaimer": "ผลตอบแทนในอดีตไม่ได้การันตีผลตอบแทนในอนาคต",
    "disclaimerColor": "#8A8F9C",
    "disclaimerFontSize": 14,
    "footer": {
      "label": "รายละเอียดเพิ่มเติม คลิก",
      "labelColor": "#1F2A44",
      "labelFontSize": 14,
      "linkLabel": "กองทุน One-elite11-ra",
      "linkLabelColor": "#1F2A44",
      "linkLabelFontSize": 14,
      "linkUrl": "https://www.lhfund.co.th/"
    },
    "mainFrameColor": "#FFFFFF"
  }
}`;

/**
 * type9 — funds side by side: `funds[]` are the columns, `rows[]` the attributes compared. The first
 * column is frozen while the rest scrolls sideways, and `scrollHint` is the line that says so.
 */
export const COMPARISON_SAMPLE = `{
  "type": "TAX_FUND_COMPARISON",
  "version": 1,
  "refId": "type9",
  "data": {
    "title": "สามารถเปรียบเทียบผลการดำเนินงานของกองทุน ONE-ELITE11RA, KKP-GNP, SCBGOLD และ K-USA ดังนี้ 👇",
    "titleColor": "#1F2A44",
    "titleFontSize": 16,
    "tableHeight": 200,
    "rowHeaderLabel": "รายการ",
    "rowHeaderLabelColor": "#1F2A44",
    "rowHeaderLabelFontSize": 16,
    "funds": [
      "ONE-ELITE11RA",
      "KKP-GNP",
      "SCBGOLD",
      "K-USA"
    ],
    "fundsColor": "#1F2A44",
    "fundsFontSize": 14,
    "rows": [
      {
        "rowFrameColor": "#EAF1E6",
        "label": "Morningstar Rating",
        "labelColor": "#1F2A44",
        "labelFontSize": 14,
        "cells": [
          {
            "rowFrameColor": "#EAF1E6",
            "value": "★★★★☆",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          },
          {
            "rowFrameColor": "#EAF1E6",
            "value": "★★★☆☆",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          },
          {
            "rowFrameColor": "#EAF1E6",
            "value": "★★★★★",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          },
          {
            "rowFrameColor": "#EAF1E6",
            "value": "★★★☆☆",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          }
        ]
      },
      {
        "rowFrameColor": "#EAF1E6",
        "label": "ประเภทกองทุน",
        "labelColor": "#1F2A44",
        "labelFontSize": 14,
        "cells": [
          {
            "rowFrameColor": "#EAF1E6",
            "value": "ผสม (หุ้น+ตราสารหนี้)",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          },
          {
            "rowFrameColor": "#EAF1E6",
            "value": "หุ้นต่างประเทศ",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          },
          {
            "rowFrameColor": "#EAF1E6",
            "value": "ทองคำ",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          },
          {
            "rowFrameColor": "#EAF1E6",
            "value": "หุ้นต่างประเทศ",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          }
        ]
      },
      {
        "rowFrameColor": "#EAF1E6",
        "label": "ระดับความเสี่ยง",
        "labelColor": "#1F2A44",
        "labelFontSize": 14,
        "cells": [
          {
            "rowFrameColor": "#EAF1E6",
            "value": "5",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          },
          {
            "rowFrameColor": "#EAF1E6",
            "value": "6",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          },
          {
            "rowFrameColor": "#EAF1E6",
            "value": "8",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          },
          {
            "rowFrameColor": "#EAF1E6",
            "value": "6",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          }
        ]
      },
      {
        "rowFrameColor": "#EAF1E6",
        "label": "ผลตอบแทน 1 ปี",
        "labelColor": "#1F2A44",
        "labelFontSize": 14,
        "cells": [
          {
            "rowFrameColor": "#EAF1E6",
            "value": "+6.82%",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          },
          {
            "rowFrameColor": "#EAF1E6",
            "value": "+9.10%",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          },
          {
            "rowFrameColor": "#EAF1E6",
            "value": "+12.30%",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          },
          {
            "rowFrameColor": "#EAF1E6",
            "value": "+15.40%",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          }
        ]
      },
      {
        "rowFrameColor": "#EAF1E6",
        "label": "ผลตอบแทนตั้งแต่จัดตั้งกองทุน",
        "labelColor": "#1F2A44",
        "labelFontSize": 14,
        "cells": [
          {
            "rowFrameColor": "#EAF1E6",
            "value": "+5.40%",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          },
          {
            "rowFrameColor": "#EAF1E6",
            "value": "+7.90%",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          },
          {
            "rowFrameColor": "#EAF1E6",
            "value": "+4.20%",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          },
          {
            "rowFrameColor": "#EAF1E6",
            "value": "+11.10%",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          }
        ]
      },
      {
        "rowFrameColor": "#EAF1E6",
        "label": "SD (1 ปี)",
        "labelColor": "#1F2A44",
        "labelFontSize": 14,
        "cells": [
          {
            "rowFrameColor": "#EAF1E6",
            "value": "12.4%",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          },
          {
            "rowFrameColor": "#EAF1E6",
            "value": "15.8%",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          },
          {
            "rowFrameColor": "#EAF1E6",
            "value": "18.2%",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          },
          {
            "rowFrameColor": "#EAF1E6",
            "value": "16.0%",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          }
        ]
      },
      {
        "rowFrameColor": "#EAF1E6",
        "label": "Sharpe Ratio (1 ปี)",
        "labelColor": "#1F2A44",
        "labelFontSize": 14,
        "cells": [
          {
            "rowFrameColor": "#EAF1E6",
            "value": "0.58",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          },
          {
            "rowFrameColor": "#EAF1E6",
            "value": "0.71",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          },
          {
            "rowFrameColor": "#EAF1E6",
            "value": "0.42",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          },
          {
            "rowFrameColor": "#EAF1E6",
            "value": "0.88",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          }
        ]
      },
      {
        "rowFrameColor": "#EAF1E6",
        "label": "Maximum Drawdown (1 ปี)",
        "labelColor": "#1F2A44",
        "labelFontSize": 14,
        "cells": [
          {
            "rowFrameColor": "#EAF1E6",
            "value": "-9.2%",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          },
          {
            "rowFrameColor": "#EAF1E6",
            "value": "-14.5%",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          },
          {
            "rowFrameColor": "#EAF1E6",
            "value": "-20.1%",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          },
          {
            "rowFrameColor": "#EAF1E6",
            "value": "-12.0%",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          }
        ]
      },
      {
        "rowFrameColor": "#EAF1E6",
        "label": "นโยบายการจ่ายปันผล",
        "labelColor": "#1F2A44",
        "labelFontSize": 14,
        "cells": [
          {
            "rowFrameColor": "#EAF1E6",
            "value": "ไม่จ่ายปันผล",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          },
          {
            "rowFrameColor": "#EAF1E6",
            "value": "จ่ายปันผล",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          },
          {
            "rowFrameColor": "#EAF1E6",
            "value": "ไม่จ่ายปันผล",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          },
          {
            "rowFrameColor": "#EAF1E6",
            "value": "จ่ายปันผล",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          }
        ]
      }
    ],
    "scrollHint": "เลื่อนซ้าย-ขวาเพื่อดูกองทุนอื่น",
    "scrollHintColor": "#8A8F9C",
    "scrollHintFontSize": 14,
    "disclaimer": "ผลตอบแทนในอดีตไม่ได้การันตีผลตอบแทนในอนาคต และกองทุนทั้งสองมีระดับความเสี่ยงและสินทรัพย์ที่ลงทุนต่างกัน ควรพิจารณาให้เหมาะกับเป้าหมายของตนเอง",
    "disclaimerColor": "#8A8F9C",
    "disclaimerFontSize": 14,
    "footer": {
      "label": "ดูรายละเอียดฉบับเต็ม คลิก",
      "labelColor": "#1F2A44",
      "labelFontSize": 14,
      "separator": ", ",
      "lastSeparator": " หรือ ",
      "links": [
        {
          "rowFrameColor": "#EAF1E6",
          "label": "ONE-ELITE11RA",
          "labelColor": "#1F2A44",
          "labelFontSize": 14,
          "url": "https://www.lhfund.co.th/"
        },
        {
          "rowFrameColor": "#EAF1E6",
          "label": "KKP-GNP",
          "labelColor": "#1F2A44",
          "labelFontSize": 14,
          "url": "https://www.kkpfg.com/"
        },
        {
          "rowFrameColor": "#EAF1E6",
          "label": "SCBGOLD",
          "labelColor": "#1F2A44",
          "labelFontSize": 14,
          "url": "https://www.scbam.com/"
        },
        {
          "rowFrameColor": "#EAF1E6",
          "label": "K-USA",
          "labelColor": "#1F2A44",
          "labelFontSize": 14,
          "url": "https://www.kasikornasset.com/"
        }
      ]
    },
    "mainFrameColor": "#FFFFFF"
  }
}`;

/**
 * type10 — the performance table, in the FLAT shape: `periodHeaderLabel` + `columns[]` for the head,
 * `rows[].period` + `rows[].cells[]` for the body, `caption` underneath, `tableHeight` capping it.
 *
 * 🔴 NOT the `{ factsheet, performance }` shape written up in the tax-card-types document. That
 * describes a different envelope; this is the one the product emits and the one the owner signed off
 * on, and the renderer is built against it.
 */
export const PERFORMANCE_SAMPLE = `{
  "type": "TAX_FUND_PERFORMANCE",
  "version": 1,
  "refId": "type10",
  "data": {
    "title": "ผลตอบแทนและความเสี่ยง",
    "titleColor": "#1F2A44",
    "titleFontSize": 16,
    "tableHeight": 400,
    "periodHeaderLabel": "ช่วงเวลา",
    "periodHeaderLabelColor": "#1F2A44",
    "periodHeaderLabelFontSize": 16,
    "columns": [
      "Total Return",
      "SD",
      "Sharpe Ratio",
      "Max Drawdown"
    ],
    "columnsColor": "#1F2A44",
    "columnsFontSize": 14,
    "rows": [
      {
        "rowFrameColor": "#EAF1E6",
        "period": "YTD",
        "periodColor": "#1F2A44",
        "periodFontSize": 14,
        "cells": [
          {
            "rowFrameColor": "#EAF1E6",
            "value": "33.96%",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          },
          {
            "rowFrameColor": "#EAF1E6",
            "value": "-",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          },
          {
            "rowFrameColor": "#EAF1E6",
            "value": "-",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          },
          {
            "rowFrameColor": "#EAF1E6",
            "value": "-",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          }
        ]
      },
      {
        "rowFrameColor": "#EAF1E6",
        "period": "1M",
        "periodColor": "#1F2A44",
        "periodFontSize": 14,
        "cells": [
          {
            "rowFrameColor": "#EAF1E6",
            "value": "2.93%",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          },
          {
            "rowFrameColor": "#EAF1E6",
            "value": "-",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          },
          {
            "rowFrameColor": "#EAF1E6",
            "value": "-",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          },
          {
            "rowFrameColor": "#EAF1E6",
            "value": "-",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          }
        ]
      },
      {
        "rowFrameColor": "#EAF1E6",
        "period": "2M",
        "periodColor": "#1F2A44",
        "periodFontSize": 14,
        "cells": [
          {
            "rowFrameColor": "#EAF1E6",
            "value": "6.89%",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          },
          {
            "rowFrameColor": "#EAF1E6",
            "value": "-",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          },
          {
            "rowFrameColor": "#EAF1E6",
            "value": "-",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          },
          {
            "rowFrameColor": "#EAF1E6",
            "value": "-",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          }
        ]
      },
      {
        "rowFrameColor": "#EAF1E6",
        "period": "3M",
        "periodColor": "#1F2A44",
        "periodFontSize": 14,
        "cells": [
          {
            "rowFrameColor": "#EAF1E6",
            "value": "31.11%",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          },
          {
            "rowFrameColor": "#EAF1E6",
            "value": "34.75%",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          },
          {
            "rowFrameColor": "#EAF1E6",
            "value": "-",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          },
          {
            "rowFrameColor": "#EAF1E6",
            "value": "-",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          }
        ]
      },
      {
        "rowFrameColor": "#EAF1E6",
        "period": "6M",
        "periodColor": "#1F2A44",
        "periodFontSize": 14,
        "cells": [
          {
            "rowFrameColor": "#EAF1E6",
            "value": "46.34%",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          },
          {
            "rowFrameColor": "#EAF1E6",
            "value": "34.28%",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          },
          {
            "rowFrameColor": "#EAF1E6",
            "value": "-",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          },
          {
            "rowFrameColor": "#EAF1E6",
            "value": "-",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          }
        ]
      },
      {
        "rowFrameColor": "#EAF1E6",
        "period": "1Y",
        "periodColor": "#1F2A44",
        "periodFontSize": 14,
        "cells": [
          {
            "rowFrameColor": "#EAF1E6",
            "value": "31.66%",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          },
          {
            "rowFrameColor": "#EAF1E6",
            "value": "33.89%",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          },
          {
            "rowFrameColor": "#EAF1E6",
            "value": "0.53",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          },
          {
            "rowFrameColor": "#EAF1E6",
            "value": "-21.08%",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          }
        ]
      },
      {
        "rowFrameColor": "#EAF1E6",
        "period": "3Y",
        "periodColor": "#1F2A44",
        "periodFontSize": 14,
        "cells": [
          {
            "rowFrameColor": "#EAF1E6",
            "value": "20.56%",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          },
          {
            "rowFrameColor": "#EAF1E6",
            "value": "24.12%",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          },
          {
            "rowFrameColor": "#EAF1E6",
            "value": "0.60",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          },
          {
            "rowFrameColor": "#EAF1E6",
            "value": "-22.50%",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          }
        ]
      },
      {
        "rowFrameColor": "#EAF1E6",
        "period": "5Y",
        "periodColor": "#1F2A44",
        "periodFontSize": 14,
        "cells": [
          {
            "rowFrameColor": "#EAF1E6",
            "value": "5.21%",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          },
          {
            "rowFrameColor": "#EAF1E6",
            "value": "-",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          },
          {
            "rowFrameColor": "#EAF1E6",
            "value": "-",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          },
          {
            "rowFrameColor": "#EAF1E6",
            "value": "-",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          }
        ]
      },
      {
        "rowFrameColor": "#EAF1E6",
        "period": "Since Inception",
        "periodColor": "#1F2A44",
        "periodFontSize": 14,
        "cells": [
          {
            "rowFrameColor": "#EAF1E6",
            "value": "4.95%",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          },
          {
            "rowFrameColor": "#EAF1E6",
            "value": "-",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          },
          {
            "rowFrameColor": "#EAF1E6",
            "value": "-",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          },
          {
            "rowFrameColor": "#EAF1E6",
            "value": "-",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          }
        ]
      }
    ],
    "caption": "ข้อมูล ณ 8 ส.ค. 2569",
    "captionColor": "#8A8F9C",
    "captionFontSize": 14,
    "mainFrameColor": "#FFFFFF"
  }
}`;

/**
 * type14 — the fund PICKER, single-select: the card the customer answers when they asked ABOUT a fund
 * and the search matched more than one. The phone lists `list`, the customer taps one, and what comes
 * back is an ordinary chat message: `prefix + code`.
 *
 * 🔴 TWO STORED CONFIGS, ONE PER MODE — this one and {@link PICKER_COMPARE_SAMPLE}. The node carries a
 * code for each (`mutualFundPickerConfigCode` / `mutualFundPickerCompareConfigCode`), so each document
 * states its own mode in `cp` and nothing is decided at run time. That is what lets `prefix` and
 * `submit` be EDITABLE, TRANSLATED text rather than constants compiled into the server.
 *
 * 🔴 `join` AND `submit` ARE EMPTY ON PURPOSE, not unfinished. A single-select picker sends exactly one
 * code (nothing to join) and fires on the tap (no button to caption). An empty `submit` is how the wire
 * says "draw no button".
 *
 * 🔴 THE TRAILING SPACE IN `prefix` IS PART OF THE VALUE. The message is a plain concatenation, so
 * "ขอดูข้อมูลกองทุน " without it produces "ขอดูข้อมูลกองทุนABALL-TESGX1". It is also load-bearing in a
 * second way: the sentence comes straight back through this node's extractor, and the single-select
 * wording is what makes the next turn read `cp=false`.
 *
 * 🔴 ALL THREE LANGUAGES, EVERY SEAT. A bag missing one language sends the WHOLE CARD back to Thai for
 * that customer — the collapser is all-or-nothing per card, not per field.
 *
 * `list` is the one templated part, and has to be: the options are whatever this turn's search found.
 * `@omitWhenNull` keeps a fund with no code out of the array instead of putting a null in it that the
 * phone would draw as a blank chip.
 */
export const PICKER_SAMPLE = `{
  "type": "MF",
  "version": 1,
  "refId": "type14",
  "cp": false,
  "prefix": {
    "th": "ขอดูข้อมูลกองทุน ",
    "en": "Show fund ",
    "zh": "查看基金 "
  },
  "join": "",
  "submit": "",
  "list": {
    "@repeat": "mf.funds",
    "@template": {
      "@omitWhenNull": "$.fundCode",
      "@value": "{{ $.fundCode }}"
    }
  }
}`;

/**
 * type14 — the fund PICKER, compare: the customer asked to COMPARE but the search matched more funds
 * than they named, so this one lets them tick several and press the button. What comes back is
 * `prefix + codes.join(join)`, and the next turn — now that the funds are unambiguous — draws the
 * comparison card (type9).
 *
 * The twin of {@link PICKER_SAMPLE}; everything in that note applies here too. What differs is exactly
 * what the mode changes:
 *   • `cp: true`   — the app draws tick boxes and a button instead of single-tap chips
 *   • `join: ", "` — the separator BETWEEN the chosen codes, with its space; blank would weld them
 *   • `submit`     — the button's caption, which single-select has no use for
 *   • `prefix`     — "ขอเปรียบเทียบกองทุน ", the wording that makes the next turn read `cp=true`
 *
 * 🔴 Shipping the single-select wording here is the one edit that breaks the loop guard: the sentence
 * would re-read as `cp=false`, the node would match several funds again, and the picker would come
 * back forever instead of the comparison card.
 */
export const PICKER_COMPARE_SAMPLE = `{
  "type": "MF",
  "version": 1,
  "refId": "type14",
  "cp": true,
  "prefix": {
    "th": "ขอเปรียบเทียบกองทุน ",
    "en": "Compare funds ",
    "zh": "比较基金 "
  },
  "join": ", ",
  "submit": {
    "th": "เลือกเปรียบเทียบ",
    "en": "Compare selected",
    "zh": "比较所选"
  },
  "list": {
    "@repeat": "mf.funds",
    "@template": {
      "@omitWhenNull": "$.fundCode",
      "@value": "{{ $.fundCode }}"
    }
  }
}`;

/**
 * The menu, in the order it is offered.
 *
 * `label` is what the admin clicks; `cardType` is what `detectCardType` will report once that JSON
 * lands in the box, which is what the row chip shows — so a sample whose `cardType` here disagrees
 * with its own `type` key is a bug you can see. `refId` is likewise the document's own.
 *
 * 🔴 `key` EXISTS BECAUSE THE LAST TWO ENTRIES SHARE BOTH `cardType` AND `refId`. They are two
 * documents of ONE card type — the picker's two modes — so neither of those identifies a menu row
 * any more, and using one as the `v-for` key would give Vue duplicate keys (and the two rows one
 * `data-testid` between them). `key` is the menu's own identity and nothing else reads it; it
 * matches `refId` for the three cards that have no twin, so their test ids are unchanged.
 */
export const CARD_CONFIG_SAMPLES = [
  { key: 'type8', cardType: 'TAX_FUND_FACTSHEET', refId: 'type8', label: 'Fund factsheet (type8)', json: FACTSHEET_SAMPLE },
  { key: 'type9', cardType: 'TAX_FUND_COMPARISON', refId: 'type9', label: 'Fund comparison (type9)', json: COMPARISON_SAMPLE },
  { key: 'type10', cardType: 'TAX_FUND_PERFORMANCE', refId: 'type10', label: 'Fund performance (type10)', json: PERFORMANCE_SAMPLE },
  {
    key: 'type14',
    cardType: 'MF',
    refId: 'type14',
    label: 'Fund picker (type14) — เจอหลายกอง ไม่ได้ขอเทียบ',
    json: PICKER_SAMPLE,
  },
  {
    key: 'type14-compare',
    cardType: 'MF',
    refId: 'type14',
    label: 'Fund picker (type14) — ขอเทียบ เลือกได้หลายกอง',
    json: PICKER_COMPARE_SAMPLE,
  },
];

export default {
  FACTSHEET_SAMPLE,
  COMPARISON_SAMPLE,
  PERFORMANCE_SAMPLE,
  PICKER_SAMPLE,
  PICKER_COMPARE_SAMPLE,
  CARD_CONFIG_SAMPLES,
};
